import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { Button, Modal } from './ui';
import { IconCheck, IconAlert, IconInfo, IconClose } from './icons';

/**
 * App-level overlays for the admin panel: toasts and confirmation dialogs.
 *
 * These replace window.alert()/window.confirm(), which block the page, cannot be
 * styled, and give no room for context. useConfirm() returns a promise so calling
 * code keeps the same shape it had with window.confirm().
 */

const ToastContext = createContext(null);
const ConfirmContext = createContext(null);

const toastTones = {
  success: {
    icon: IconCheck,
    ring: 'ring-success-100',
    iconWrap: 'bg-success-50 text-success-700'
  },
  error: {
    icon: IconAlert,
    ring: 'ring-danger-100',
    iconWrap: 'bg-danger-50 text-danger-700'
  },
  info: {
    icon: IconInfo,
    ring: 'ring-info-100',
    iconWrap: 'bg-info-50 text-info-700'
  }
};

export const AdminOverlaysProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  const [confirmState, setConfirmState] = useState(null);
  const resolverRef = useRef(null);
  const idRef = useRef(0);

  const dismiss = useCallback((id) => {
    setToasts((current) => current.filter((t) => t.id !== id));
  }, []);

  const toast = useCallback(
    (message, { tone = 'success', title, duration = 4500 } = {}) => {
      idRef.current += 1;
      const id = idRef.current;
      setToasts((current) => [...current, { id, message, tone, title }]);
      if (duration) {
        setTimeout(() => dismiss(id), duration);
      }
      return id;
    },
    [dismiss]
  );

  const confirm = useCallback((options) => {
    setConfirmState(typeof options === 'string' ? { message: options } : options);
    return new Promise((resolve) => {
      resolverRef.current = resolve;
    });
  }, []);

  const settle = useCallback((result) => {
    setConfirmState(null);
    resolverRef.current?.(result);
    resolverRef.current = null;
  }, []);

  const toastApi = useMemo(() => ({ toast, dismiss }), [toast, dismiss]);

  return (
    <ToastContext.Provider value={toastApi}>
      <ConfirmContext.Provider value={confirm}>
        {children}

        {/* Toast stack */}
        <div
          className="pointer-events-none fixed inset-x-0 bottom-0 z-[60] flex flex-col items-center gap-2 p-4 sm:inset-x-auto sm:right-0 sm:top-0 sm:items-end"
          role="status"
          aria-live="polite"
        >
          {toasts.map(({ id, message, tone, title }) => {
            const { icon: Icon, ring, iconWrap } = toastTones[tone] || toastTones.info;
            return (
              <div
                key={id}
                className={`animate-rise pointer-events-auto flex w-full max-w-sm items-start gap-3 rounded-xl bg-white p-3.5 shadow-panel ring-1 ${ring}`}
              >
                <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${iconWrap}`}>
                  <Icon size={16} />
                </span>
                <div className="min-w-0 flex-1 pt-0.5">
                  {title && <p className="text-sm font-600 text-ink-900">{title}</p>}
                  <p className="break-words text-sm text-ink-600">{message}</p>
                </div>
                <button
                  type="button"
                  onClick={() => dismiss(id)}
                  aria-label="Dismiss notification"
                  className="-mr-1 -mt-1 rounded-md p-1.5 text-ink-500 transition-colors hover:bg-ink-100 hover:text-ink-700"
                >
                  <IconClose size={14} />
                </button>
              </div>
            );
          })}
        </div>

        {/* Confirmation dialog */}
        <Modal
          open={Boolean(confirmState)}
          onClose={() => settle(false)}
          title={confirmState?.title || 'Please confirm'}
          size="sm"
          footer={
            <>
              <Button variant="secondary" onClick={() => settle(false)}>
                {confirmState?.cancelLabel || 'Cancel'}
              </Button>
              <Button
                variant={confirmState?.tone === 'danger' ? 'danger' : 'primary'}
                onClick={() => settle(true)}
              >
                {confirmState?.confirmLabel || 'Confirm'}
              </Button>
            </>
          }
        >
          <p className="text-sm leading-relaxed text-ink-600">{confirmState?.message}</p>
          {confirmState?.detail && (
            <p className="mt-3 rounded-lg bg-ink-50 px-3 py-2.5 text-xs text-ink-600">
              {confirmState.detail}
            </p>
          )}
        </Modal>
      </ConfirmContext.Provider>
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) throw new Error('useToast must be used inside AdminOverlaysProvider');
  return context.toast;
};

export const useConfirm = () => {
  const context = useContext(ConfirmContext);
  if (!context) throw new Error('useConfirm must be used inside AdminOverlaysProvider');
  return context;
};
